import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type Dispatch,
  type ForwardedRef,
  type ReactNode,
  type SetStateAction,
} from "react";
import { cn } from "../../utils";
import type { ChipListHandle } from "./chip-suggestion";

// Shared selection + scroll behavior for every chip suggestion popup list
// (the AOP `@` popup, `@mention`, the `{{` variable/context popups). The
// duplication these replace was the bug: ArrowUp/ArrowDown moved a
// `selectedIndex` in four near-identical copies, but only some of the
// containers actually scrolled and NONE kept the highlighted row in view.
// Family list components now own only their row rendering/grouping; this
// module owns the popup chrome (scroll container) and the
// selection/keyboard/scroll mechanics.

export interface UseSuggestionListNavResult<T extends HTMLElement> {
  selectedIndex: number;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  /**
   * Ref callback for row `index`. Attach to every rendered row — it's a
   * no-op for rows other than the current selection. Wiring it this way
   * (rather than each caller conditionally passing `undefined`) keeps the
   * per-list JSX identical whether or not a row happens to be selected.
   */
  itemRef: (index: number) => (node: T | null) => void;
}

/**
 * Owns the parts of a chip suggestion popup list that must not vary per
 * family: a wrapping `selectedIndex` driven by ArrowUp/ArrowDown, Enter/Tab
 * committing the selection via `onSelect`, resetting to `defaultIndex`
 * whenever `resetKey` changes (typically the list's items/rows), and
 * scrolling the selected row into view as the selection moves.
 *
 * `defaultIndex` is 0 for lists whose first row is a real match, or `-1`
 * when nothing should highlight until hover/arrows (type picker). Seeding
 * it here — not a follow-up effect — keeps the reset a single write.
 *
 * `block: "nearest"` is load-bearing: plain `scrollIntoView()` also scrolls
 * ancestor scroll containers (the page, an enclosing dialog) — this must
 * only move the popup's own scroll container.
 *
 * Forwards the imperative {@link ChipListHandle} every popup list must
 * expose to Tiptap's suggestion plugin.
 */
export function useSuggestionListNav<T extends HTMLElement = HTMLElement>(
  ref: ForwardedRef<ChipListHandle>,
  count: number,
  onSelect: (index: number) => void,
  resetKey?: unknown,
  defaultIndex = 0,
  onExtraKey?: (event: KeyboardEvent, selectedIndex: number) => boolean,
): UseSuggestionListNavResult<T> {
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
  // Every row's node, keyed by index — not just the selected one. Tracking
  // only the selection would make the ref writes order-dependent: on ArrowUp
  // the detach of the previously selected (higher-index) row can land after
  // the attach of the new one and null out the node we just recorded.
  const nodesRef = useRef(new Map<number, T>());

  useEffect(() => {
    setSelectedIndex(defaultIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, defaultIndex]);

  useEffect(() => {
    if (selectedIndex < 0) return;
    nodesRef.current.get(selectedIndex)?.scrollIntoView?.({ block: "nearest" });
  }, [selectedIndex]);

  const itemRef = (index: number) => (node: T | null) => {
    if (node) nodesRef.current.set(index, node);
    else nodesRef.current.delete(index);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown(event) {
      // Family-specific keys get first refusal, so a list can claim a key the
      // shared mechanics don't use (the entity popup's ArrowRight, which
      // drills from a custom model into its fields). Returning false falls
      // through to the shared handling below — this cannot shadow
      // ArrowUp/Down/Enter/Tab unless a family deliberately claims them.
      if (onExtraKey?.(event, selectedIndex)) return true;
      if (event.key === "ArrowDown") {
        if (count === 0) return true;
        setSelectedIndex((prev) => (prev + 1) % count);
        return true;
      }
      if (event.key === "ArrowUp") {
        if (count === 0) return true;
        setSelectedIndex((prev) => (prev - 1 < 0 ? count - 1 : prev - 1));
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        if (count === 0 || selectedIndex < 0) return false;
        onSelect(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  return { selectedIndex, setSelectedIndex, itemRef };
}

export interface SuggestionRowProps<T extends HTMLElement = HTMLButtonElement> {
  /** This row's position in the flattened selectable list. */
  index: number;
  selectedIndex: number;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  /** `itemRef` from {@link useSuggestionListNav} — passed whole, not pre-applied,
   *  so a row never has to know it's the selected one. */
  itemRef: (index: number) => (node: T | null) => void;
  onSelect: (index: number) => void;
  /** Action rows (create / add) read as secondary until highlighted. */
  muted?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * One selectable row in a chip suggestion popup. Every family's rows share the
 * same hit area, highlight, and — critically — the `onMouseDown` preventDefault
 * that stops the click from blurring the editor and tearing down the suggestion
 * session before `onSelect` runs. That last detail is why this is a component
 * and not a copied className: it's invisible in review and breaks the row
 * silently when omitted.
 */
export function SuggestionRow<T extends HTMLElement = HTMLButtonElement>({
  index,
  selectedIndex,
  setSelectedIndex,
  itemRef,
  onSelect,
  muted,
  className,
  children,
}: SuggestionRowProps<T>) {
  return (
    <button
      ref={itemRef(index) as (node: HTMLButtonElement | null) => void}
      type="button"
      className={cn(
        "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none",
        muted && "text-muted-foreground",
        index === selectedIndex && "bg-accent text-accent-foreground",
        className,
      )}
      onMouseDown={(e) => e.preventDefault()}
      onTouchStart={(e) => e.preventDefault()}
      onTouchEnd={(e) => {
        e.preventDefault();
        onSelect(index);
      }}
      onMouseEnter={() => setSelectedIndex(index)}
      onClick={() => onSelect(index)}
    >
      {children}
    </button>
  );
}

export interface SuggestionListContainerProps {
  /** Extra classes — each family sets its own popup class hook (e.g.
   *  `entity-ref-popup`) plus width (lists vary w-72 vs w-80). */
  className?: string;
  /** When true, renders `emptyMessage` in place of `children`. */
  empty?: boolean;
  emptyMessage?: ReactNode;
  /** Text size for the empty-state row; families differ (text-sm vs text-xs). */
  emptyClassName?: string;
  /** Sticky chrome above the scrolling rows (search, breadcrumbs). */
  header?: ReactNode;
  children?: ReactNode;
}

/**
 * The scroll container every chip suggestion popup renders into:
 * bounded height + `overflow-y-auto` (so `scrollIntoView` has a container to
 * scroll) and the shared empty state. Family lists supply only their rows.
 */
export function SuggestionListContainer({
  className,
  empty,
  emptyMessage,
  emptyClassName,
  header,
  children,
}: SuggestionListContainerProps) {
  return (
    <div
      className={cn(
        "z-50 rounded-md border bg-popover text-popover-foreground shadow-md",
        className,
      )}
    >
      {header ? <div className="border-b border-border/60 px-1.5 py-1">{header}</div> : null}
      <div className="max-h-72 overflow-y-auto p-1">
        {empty ? (
          <div className={cn("px-2 py-1.5 text-sm text-muted-foreground", emptyClassName)}>
            {emptyMessage}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
